// src/components/Business/BusinessProfileForm.jsx
import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import api from '../../api/axios';
import LocationSelector from './LocationSelector'; // 👈 IMPORTANTE

const BusinessProfileForm = () => {
  const [business, setBusiness] = useState(null);
  const [rubros, setRubros] = useState([]);            // ⭐ NUEVO
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [geoMessage, setGeoMessage] = useState('');
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Traemos perfil + rubros en paralelo
        const [bizRes, rubrosRes] = await Promise.all([
          api.get('/business/me'),
          api.get('/rubros'),
        ]);

        const biz = bizRes.data.business || {};
        if (!biz.country) biz.country = 'AR';

        setBusiness(biz);

        const rubrosData = rubrosRes.data.data || rubrosRes.data || [];
        setRubros(rubrosData);
      } catch (err) {
        console.error('Error al traer perfil o rubros:', err);
        setError('No pudimos cargar el perfil del negocio.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBusiness((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (field, value) => {
    setBusiness((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!business) return;

    setSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      if (business.name) formData.append('name', business.name);
      if (business.address) formData.append('address', business.address);
      if (business.postal_code !== undefined) formData.append('postal_code', business.postal_code || '');
      if (business.latitude !== undefined && business.latitude !== null && business.latitude !== '') {
        formData.append('latitude', business.latitude);
      }
      if (business.longitude !== undefined && business.longitude !== null && business.longitude !== '') {
        formData.append('longitude', business.longitude);
      }
      if (business.description) formData.append('description', business.description);

      // Ubicación
      if (business.country) formData.append('country', business.country);
      if (business.state) formData.append('state', business.state);
      if (business.city) formData.append('city', business.city);

      // Rubro principal
      if (
        business.rubro_id !== undefined &&
        business.rubro_id !== null &&
        business.rubro_id !== ''
      ) {
        formData.append('rubro_id', business.rubro_id);
      }

      if (logoFile) formData.append('logo', logoFile);

      const response = await api.put('/business/me', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response?.data?.geocode?.auto_applied) {
        setBusiness((prev) => ({
          ...prev,
          latitude: response.data.geocode.latitude,
          longitude: response.data.geocode.longitude,
        }));
        setGeoMessage('Ubicacion actualizada automaticamente desde la direccion.');
      } else if (response?.data?.geocode_warning) {
        setGeoMessage(response.data.geocode_warning);
      } else {
        setGeoMessage('');
      }

      alert('Perfil del negocio actualizado.');
    } catch (err) {
      console.error('Error guardando perfil del negocio:', err);
      setError('No pudimos guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalizacion.');
      return;
    }

    setGeoLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude).toFixed(6);
        const lng = Number(pos.coords.longitude).toFixed(6);
        setBusiness((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        setGeoMessage('Coordenadas cargadas con tu ubicacion actual.');
        setGeoLoading(false);
      },
      () => {
        setError('No pudimos obtener tu ubicacion actual.');
        setGeoLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  };

  const handleGeocodeFromAddress = async () => {
    if (!business) return;

    setGeocodeLoading(true);
    setError(null);
    setGeoMessage('');

    try {
      const payload = {
        address: business.address || '',
        postal_code: business.postal_code || '',
        city: business.city || '',
        state: business.state || '',
        country: business.country || '',
      };

      const { data } = await api.post('/business/geocode-address', payload);
      setBusiness((prev) => ({
        ...prev,
        latitude: Number(data.latitude).toFixed(6),
        longitude: Number(data.longitude).toFixed(6),
      }));
      setGeoMessage('Coordenadas obtenidas desde direccion y codigo postal.');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'No pudimos calcular coordenadas con esa direccion.';
      setError(msg);
    } finally {
      setGeocodeLoading(false);
    }
  };

  const lat = Number(business?.latitude);
  const lng = Number(business?.longitude);
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
  const mapDelta = 0.0035;
  const mapSrc = hasCoordinates
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - mapDelta}%2C${lat - mapDelta}%2C${lng + mapDelta}%2C${lat + mapDelta}&layer=mapnik&marker=${lat}%2C${lng}`
    : '';

  // 🔹 Skeleton mientras carga
  if (loading) {
    return (
      <Card className="mt-3 shadow-sm">
        <Card.Body>
          {/* Título skeleton */}
          <div
            style={{
              height: 20,
              width: '35%',
              background: '#e5e7eb',
              borderRadius: 4,
              marginBottom: 20,
            }}
          />

          {/* Campo: nombre local */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '25%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                height: 38,
                width: '100%',
                background: '#e5e7eb',
                borderRadius: 6,
              }}
            />
          </div>

          {/* Campo: rubro */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '30%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                height: 38,
                width: '60%',
                background: '#e5e7eb',
                borderRadius: 6,
              }}
            />
          </div>

          {/* Campo: descripción */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '28%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                height: 70,
                width: '100%',
                background: '#e5e7eb',
                borderRadius: 6,
              }}
            />
          </div>

          {/* Campos de ubicación (bloque compacto) */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '35%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    flex: '1 1 150px',
                    height: 38,
                    background: '#e5e7eb',
                    borderRadius: 6,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Dirección */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '30%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div
              style={{
                height: 38,
                width: '100%',
                background: '#e5e7eb',
                borderRadius: 6,
              }}
            />
          </div>

          {/* Logo */}
          <div className="mb-3">
            <div
              style={{
                height: 12,
                width: '25%',
                background: '#e5e7eb',
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#e5e7eb',
                }}
              />
              <div
                style={{
                  height: 38,
                  width: 200,
                  background: '#e5e7eb',
                  borderRadius: 6,
                }}
              />
            </div>
          </div>

          {/* Botón guardar */}
          <div
            style={{
              height: 36,
              width: 140,
              background: '#e5e7eb',
              borderRadius: 999,
              marginTop: 8,
            }}
          />
        </Card.Body>
      </Card>
    );
  }

  if (!business) {
    return (
      <Alert variant="warning" className="mt-4">
        No encontramos datos del negocio.
      </Alert>
    );
  }

  return (
    <Card className="mt-3">
      <Card.Body>
        <h4 className="mb-3">Perfil del local</h4>
        {business?.billing_status === "trial" && (
          <Alert variant="info" className="py-2">
            Prueba gratuita activa: te quedan <strong>{business.trial_days_left ?? 0}</strong> dias.
          </Alert>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Nombre del local</Form.Label>
            <Form.Control
              name="name"
              value={business.name || ''}
              onChange={handleChange}
            />
          </Form.Group>

          {/* Rubro principal */}
          <Form.Group className="mb-3">
            <Form.Label>Rubro principal</Form.Label>
            <Form.Select
              name="rubro_id"
              value={business.rubro_id || ''}
              onChange={handleChange}
            >
              <option value="">Seleccioná el rubro...</option>
              {rubros.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.icon ? `${r.icon} ` : ''}
                  {r.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Esto nos ayuda a mostrar tu local en la categoría correcta.
            </Form.Text>
          </Form.Group>

          {/* Descripción */}
          <Form.Group className="mb-3">
            <Form.Label>Descripción (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={business.description || ''}
              onChange={handleChange}
              placeholder="Contá brevemente qué ofrece tu local..."
            />
          </Form.Group>

          {/* País / Provincia / Ciudad */}
          <LocationSelector
            business={business}
            onChange={handleLocationChange}
          />

          {/* Dirección libre */}
          <Form.Group className="mb-3">
            <Form.Label>Dirección (calle y número)</Form.Label>
            <Form.Control
              name="address"
              value={business.address || ''}
              onChange={handleChange}
              placeholder="Ej: Calle 123, piso 4"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Codigo postal</Form.Label>
            <Form.Control
              name="postal_code"
              value={business.postal_code || ''}
              onChange={handleChange}
              placeholder="Ej: 1425"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="show-coordinates"
              label="Mostrar latitud y longitud manualmente"
              checked={showCoordinates}
              onChange={(e) => setShowCoordinates(e.target.checked)}
            />
          </Form.Group>
          {showCoordinates && (
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>Latitud</Form.Label>
                  <Form.Control
                    name="latitude"
                    value={business.latitude ?? ''}
                    onChange={handleChange}
                    placeholder="-34.603722"
                  />
                </Form.Group>
              </div>
              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>Longitud</Form.Label>
                  <Form.Control
                    name="longitude"
                    value={business.longitude ?? ''}
                    onChange={handleChange}
                    placeholder="-58.381592"
                  />
                </Form.Group>
              </div>
            </div>
          )}

          <div className="mb-3 d-flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={handleUseCurrentLocation}
              disabled={geoLoading}
            >
              {geoLoading ? 'Detectando ubicacion...' : 'Usar mi ubicacion actual'}
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              onClick={handleGeocodeFromAddress}
              disabled={geocodeLoading}
            >
              {geocodeLoading ? 'Calculando coordenadas...' : 'Calcular desde direccion'}
            </Button>
            {hasCoordinates && (
              <a
                className="btn btn-outline-primary"
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rel="noreferrer"
              >
                Ver punto en mapa
              </a>
            )}
          </div>
          {geoMessage && (
            <Alert variant="info" className="py-2">
              {geoMessage}
            </Alert>
          )}
          {hasCoordinates && (
            <div className="mb-3">
              <div className="small text-muted mb-2">Vista previa del punto</div>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  title="Mapa de ubicacion del local"
                  src={mapSrc}
                  style={{ width: '100%', height: 260, border: 0 }}
                  loading="lazy"
                />
              </div>
            </div>
          )}

          {/* Logo */}
          <Form.Group className="mb-3">
            <Form.Label>Logo (circular)</Form.Label>
            <div className="d-flex align-items-center gap-3">
              {business.logo_url && (
                <img
                  src={business.logo_url}
                  alt="Logo actual"
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                />
              )}
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files[0] || null)}
              />
            </div>
            <Form.Text className="text-muted">
              Ideal: imagen cuadrada, se recorta a círculo automáticamente.
            </Form.Text>
          </Form.Group>

          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default BusinessProfileForm;
